<template>
  <div class="layout-menu">
    <m-dropdown
      block
      :disabled="layoutDisabled"
      :title="$t('savedViewEditor.layout')"
    >
      <m-card-item
        icon="layout"
        :disabled="layoutDisabled"
      >
        {{ $t('savedViewEditor.layout') }}
        <template #right>
          <div :class="['_layout-type', layoutDisabled ? '-disabled' : '']">
            {{ getViewName(type) }}
          </div>
          <div class="_icon">
            <m-icon
              v-if="!layoutDisabled"
              type="down"
              :color="$colors.grey.lighten1"
              size="11"
            />
          </div>
        </template>
      </m-card-item>
      <template #overlay>
        <m-card
          list
        >
          <m-content
            class="_layout-grid"
            padding-xs
          >
            <div
              v-for="v in allowedViewTypes"
              :key="v"
              class="_layout-button-container"
            >
              <plan-tag
                v-if="v === viewType.tree"
                class="_plan-tag"
                :feature-flag="featureFlag.goalTree"
                :style="{ display: 'inline-flex' }"
              />
              <m-btn
                :button-style="{ height: '5.6rem', width: '100%' }"
                :color="isSelectViewType(v) ? 'primary' : ''"
                :outlined="isSelectViewType(v)"
                :clickable="isClickable(v)"
                :disabled="!canEdit"
                @click="type = v"
              >
                <div class="_layout-button">
                  <m-icon
                    class="_icon"
                    :type="getViewIcon(v)"
                    :color="isSelectViewType(v) ? $colors.blue.darken1 : $colors.grey.lighten1"
                    size="16"
                  />
                  <div class="_title">
                    {{ getViewName(v) }}
                  </div>
                </div>
              </m-btn>
            </div>
          </m-content>
          <div>
            <m-card-item
              v-if="displayShowParents"
              data-cy="show-parents-switch"
              :clickable="!readOnly"
              :no-hover="readOnly"
              tooltip-placement="left"
              :tooltip="readOnly ? $t('documentListHeader.lockedHint') : ''"
              @click="updateShowParents"
            >
              <m-switch
                :value="showParents"
                small
                :disabled="readOnly"
                :label="$t('goalsFilter.parent')"
              />
            </m-card-item>
            <m-card-item
              v-if="displayWrapCells"
              :clickable="!readOnly"
              :no-hover="readOnly"
              tooltip-placement="left"
              :tooltip="readOnly ? $t('documentListHeader.lockedHint') : ''"
              @click="updateWrapCells"
            >
              <m-switch
                :value="wrapCells"
                small
                :disabled="readOnly"
                :label="$t('documentListHeader.wrapCells')"
              />
            </m-card-item>
            <card-width-selector
              v-if="displayCardWidth"
              :value="cardWidth"
              :disabled="readOnly"
              @update:value="updateCardWidth"
            />
            <m-card-item
              v-if="displayWrapTitles"
              :tooltip="$t('documentListHeader.wrapTitlesTooltip')"
              tooltip-placement="left"
              @click="updateWrapTitles"
            >
              <m-switch
                :value="wrapTitles"
                small
                :disabled="readOnly"
                :label="$t('documentListHeader.wrapTitles')"
              />
            </m-card-item>
            <m-select
              class="_load-limit-select"
              :items="loadLimitOptions"
              :value="loadLimit"
              match-trigger-width
              full-width
              @update:value="updateLoadLimit"
            >
              <template #trigger>
                <m-card-item>
                  {{ loadLimitLabel }}
                  <template #right>
                    <div class="_load-limit-value">
                      {{ loadLimit }}
                    </div>
                    <m-icon
                      type="right"
                      :color="$colors.grey.lighten1"
                      size="11"
                    />
                  </template>
                </m-card-item>
              </template>
            </m-select>
          </div>
        </m-card>
      </template>
    </m-dropdown>
  </div>
</template>

<script>
import CardWidthSelector from '@/components/list/CardWidthSelector.vue';
import PlanTag from '@/components/plans/PlanTag.vue';
import useAccess from '@/composables/access/access';
import useDebounce from 'shared/composables/debounce';
import useLoadLimit from '@/composables/load-limit';
import { CARD_SIZE_MEDIUM } from '@/lib/constants';
import { featureFlag, viewType } from 'shared/constants.json';
import { iconByType } from '@/lib/saved-view/saved-view';
import { toRef } from 'vue';

export default {
  name: 'LayoutMenu',
  props: {
    entity: {
      type: Object,
      required: true,
    },
    allowedViewTypes: {
      type: Array,
      default: () => [viewType.default],
    },
    viewApplication: {
      type: String,
      default: '',
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: true,
    },
    cardWidth: {
      type: String,
      default: CARD_SIZE_MEDIUM,
    },
    wrapCells: {
      type: Boolean,
      default: false,
    },
    showParents: {
      type: Boolean,
      default: false,
    },
    wrapTitles: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update', 'update-view-params'],
  components: { PlanTag, CardWidthSelector },
  setup(props) {
    const { accountHasFeature } = useAccess();
    const { debounce } = useDebounce({ onBeforeUnmountFlush: true });
    const { loadLimitOptions, loadLimit, loadLimitLabel } = useLoadLimit(toRef(props, 'entity'));
    return {
      debounce,
      featureFlag,
      accountHasFeature,
      loadLimitOptions,
      loadLimit,
      loadLimitLabel,
    };
  },
  data() {
    return {
      type: null,
      viewType,
    };
  },
  computed: {
    canActivateGoalTree() {
      return this.accountHasFeature([featureFlag.goalTree]);
    },
    displayShowParents() {
      return this.type === viewType.cascade;
    },
    displayWrapCells() {
      return this.type === viewType.cascade;
    },
    displayCardWidth() {
      return this.type === viewType.tree || this.type === viewType.atlas;
    },
    displayWrapTitles() {
      return this.type === viewType.feed;
    },
    displayLoadLimit() {
      return this.type !== viewType.default;
    },
    layoutDisabled() {
      return this.allowedViewTypes.length === 1 && !(this.displayShowParents || this.displayWrapCells || this.displayCardWidth || this.displayWrapTitles);
    },
  },
  methods: {
    getViewIcon(value) {
      return iconByType(value);
    },
    getViewName(value) {
      return this.$t(`list.${value}`);
    },
    emitUpdate() {
      const view = {
        uid: this.entity.uid,
        isTemp: this.entity.isTemp,
        viewType: this.type,
      };

      this.$emit('update', view);
    },
    updateDebounced(wait = 0) {
      const fn = () => this.emitUpdate();
      this.debounce(fn, wait);
    },
    isSelectViewType(value) {
      return value === this.type;
    },
    isClickable(value) {
      return !(this.isSelectViewType(value) || (value === viewType.tree && !this.canActivateGoalTree));
    },
    updateCardWidth(value) {
      this.$emit('update-view-params', value, 'cardWidth');
    },
    updateWrapCells() {
      this.$emit('update-view-params', !this.wrapCells, 'wrapCells');
    },
    updateShowParents() {
      this.$emit('update-view-params', !this.showParents, 'showParents');
    },
    updateWrapTitles() {
      this.$emit('update-view-params', !this.wrapTitles, 'wrapTitles');
    },
    updateLoadLimit(value) {
      this.$emit('update-view-params', value, 'loadLimit');
    },
  },
  watch: {
    type(newVal, oldVal) {
      if (newVal === oldVal || oldVal === null) {
        return;
      }
      this.updateDebounced();
    },
  },
  created() {
    this.type = this.entity.viewType;
  },
};
</script>

<style
    scoped
    lang="scss"
    type="text/scss"
>
  .layout-menu {
    ._layout-type {
      color: $font-color-secondary;

      &.-disabled {
        color: $font-color-tertiary;
      }
    }

    ._icon {
      margin-left: .4rem;
    }
  }

  ._layout-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: .8rem;

      ._layout-button-container {
        position: relative;

        ._plan-tag {
          position: absolute;
          top: -.6rem;
          right: -.4rem;
        }

      ._layout-button {
        width: 5rem;
        height: 4rem;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;

        ._icon {
          margin-bottom: .4rem;
        }

        ._title {
            font-size: $font-size-1;
        }
      }
    }
  }

  ._load-limit-select {
    ._load-limit-value {
      color: $font-color-secondary;
      margin-right: .8rem;
    }
  }

</style>
